import React, { useState, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { getServicesManager } from '../../services';
import { Alert, Button, CircularProgress, TextField } from '@mui/material';
import { researchExamples } from './promptExamples';

type ConfirmAction = 'save' | 'delete' | null;

export function CustomResearch() {
  const services = getServicesManager();

  const [prompt, setPrompt] = useState<string>('');
  const [savedPrompt, setSavedPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [testUrl, setTestUrl] = useState<string>('');
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<Array<{header: string, content: string}> | null>(null);
  const [testMetadata, setTestMetadata] = useState<{selectedLinks: string[], collectedPages: number} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [showExamples, setShowExamples] = useState<boolean>(false);

  useEffect(() => {
    loadPrompt();
  }, []);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const result = await services.getAIResearchFormat();
      if (result && result.prompt) {
        setPrompt(result.prompt);
        setSavedPrompt(result.prompt);
      }
    } catch (err) {
      console.error('Failed to load prompt:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    setConfirmAction('save');
  };

  const handleDeleteClick = () => {
    setConfirmAction('delete');
  };

  const handleConfirm = async () => {
    if (confirmAction === 'save') {
      await performSave();
    } else if (confirmAction === 'delete') {
      await performDelete();
    }
    setConfirmAction(null);
  };

  const handleCancel = () => {
    setConfirmAction(null);
  };

  const performSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const success = await services.putAIResearchFormat(prompt || null);
      if (success) {
        setSavedPrompt(prompt);
        setSuccessMessage('Research prompt saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to save research prompt');
      }
    } catch (err) {
      console.error('Failed to save prompt:', err);
      setError('Failed to save research prompt');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const success = await services.putAIResearchFormat(null);
      if (success) {
        setPrompt('');
        setSavedPrompt('');
        setSuccessMessage('Research prompt deleted successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to delete research prompt');
      }
    } catch (err) {
      console.error('Failed to delete prompt:', err);
      setError('Failed to delete research prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testUrl || !prompt) return;

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      setTestMetadata(null);
      const result = await services.testGenerateAIResearch(testUrl, prompt);
      if (result && result.research) {
        setTestResult(result.research);
        setTestMetadata({
          selectedLinks: result.selected_links || [],
          collectedPages: result.collected_pages || 0
        });
      } else if (result && result.error) {
        setError(`Research error: ${result.error}`);
      } else {
        setError('No research returned');
      }
    } catch (err) {
      console.error('Failed to test prompt:', err);
      setError('Failed to generate test research');
    } finally {
      setTesting(false);
    }
  };

  const hasUnsavedChanges = prompt !== savedPrompt;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-3">
                {confirmAction === 'save' ? 'Save Research Prompt?' : 'Delete Research Prompt?'}
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                {confirmAction === 'save'
                  ? 'This will update the AI company research generation for all team members.'
                  : 'This will remove the custom research format for all team members.'}
              </Typography>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={confirmAction === 'delete' ? 'error' : 'primary'}
                fullWidth
                onClick={handleConfirm}
              >
                {confirmAction === 'save' ? 'Save' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="h-full p-6 overflow-auto">
        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage(null)} className="mb-4">
            {successMessage}
          </Alert>
        )}

        {/* Test Results - Only show when available */}
        {testResult && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <Typography variant="h4" color="#374151" fontWeight={500}>
                Test Results
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setTestResult(null);
                  setTestMetadata(null);
                }}
              >
                Close
              </Button>
            </div>
            {testMetadata && (
              <div className="px-6 pt-4 pb-2 bg-blue-50 border-b border-blue-200">
                <div className="flex gap-6">
                  <div>
                    <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
                      Links Selected
                    </Typography>
                    <Typography variant="smallParagraph" color="#1E40AF">
                      {testMetadata.selectedLinks.length}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
                      Pages Collected
                    </Typography>
                    <Typography variant="smallParagraph" color="#1E40AF">
                      {testMetadata.collectedPages}
                    </Typography>
                  </div>
                </div>
                {testMetadata.selectedLinks.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
                      View selected links
                    </summary>
                    <ul className="mt-2 ml-4 list-disc text-sm text-blue-600">
                      {testMetadata.selectedLinks.map((link, idx) => (
                        <li key={idx} className="mb-1">
                          <a href={link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {link}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
            <div className="p-6">
              <div className="space-y-4 max-h-96 overflow-auto">
                {testResult && testResult.map((section, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600} className="mb-2">
                      {section.header}
                    </Typography>
                    <Typography variant="smallParagraph" color="#4B5563" className="whitespace-pre-wrap">
                      {section.content}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Prompt Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-2">
                  Custom Research Prompt
                </Typography>
                <Typography variant="mediumParagraph" color="#6B7280">
                  Define what information to extract from company websites using AI. The system will intelligently select and explore relevant pages to gather the requested information.
                </Typography>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowExamples(!showExamples)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {showExamples ? 'Hide Examples' : 'View Example Prompts'}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveClick}
                  disabled={saving || !hasUnsavedChanges}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    saving || !hasUnsavedChanges
                      ? 'bg-blue-300 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                {savedPrompt && (
                  <button
                    onClick={handleDeleteClick}
                    disabled={saving}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      saving
                        ? 'bg-red-100 text-red-400 cursor-not-allowed'
                        : 'bg-white text-red-600 border border-red-300 hover:bg-red-50 hover:border-red-400'
                    }`}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Examples Section */}
            {showExamples && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Typography variant="h4" color="#374151" fontWeight={500} className="mb-2">
                  Example Research Prompts
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280" className="mb-4">
                  Click any example to use it as a starting point
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {researchExamples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(example.prompt)}
                      className="text-left p-4 bg-gray-50 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all"
                    >
                      <Typography variant="smallParagraph" color="#1F2937" fontWeight={600} className="mb-1">
                        {example.name}
                      </Typography>
                      <Typography variant="smallParagraph" color="#6B7280" fontSize="13px">
                        {example.description}
                      </Typography>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter your custom research prompt here... For example: 'Research this company and provide: company size, key products, recent funding, and decision makers.'"
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />

            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Typography variant="smallParagraph" color="#1E40AF" fontWeight={500} className="mb-1">
                How it works
              </Typography>
              <Typography variant="smallParagraph" color="#1E40AF">
                Our AI uses a 2-pass approach: First, it analyzes the company's main page and intelligently selects relevant links to explore based on your research prompt. Then, it gathers content from those pages and synthesizes findings according to your instructions.
              </Typography>
            </div>
          </div>
        </div>

        {/* URL Test Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <Typography variant="h4" color="#374151" fontWeight={500}>
              Test Your Research Prompt
            </Typography>
            <Typography variant="smallParagraph" color="#6B7280" className="mt-1">
              Enter a company website URL to test your prompt
            </Typography>
          </div>
          <div className="p-6">
            <div className="flex gap-3 mb-4">
              <TextField
                fullWidth
                value={testUrl}
                onChange={(e) => setTestUrl(e.target.value)}
                placeholder="https://example.com"
                variant="outlined"
                size="small"
              />
              <button
                onClick={handleTest}
                disabled={testing || !prompt || !testUrl}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  testing || !prompt || !testUrl
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {testing ? 'Researching...' : 'Test Research'}
              </button>
            </div>
            <Typography variant="smallParagraph" color="#6B7280">
              Tip: Try testing with companies like stripe.com, notion.so, or any company website
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
